/**
 * 上传服务模块：处理文件上传到 OSS
 * 支持单文件和批量上传，带进度追踪
 * 使用签名 URL 方式进行前端直传
 *
 * ==================== 性能优化说明（支持 1 万张图片上传） ====================
 *
 * 1. 并发数优化
 *    - 从 5 提升到 15，在网络带宽充足时显著提升上传速度
 *    - 10,000 张图片上传时间从 ~2.8 小时缩短到 ~56 分钟（假设每张 5 秒）
 *
 * 2. 最大文件数提升
 *    - 从 1,000 提升到 10,000，支持更大规模的批量上传
 *
 * ==========================================================================
 */

import { apiService } from './api'
import type {
  UploadProgress,
  UploadFileItem,
  TaskApiResponse,
} from '@/types/task'

// ==================== 类型定义 ====================

/**
 * 签名 URL 响应
 */
export interface UploadUrlResponse {
  /** 用于 PUT 上传的签名 URL */
  uploadUrl: string
  /** 文件在 OSS 上的 Object Key */
  objectKey: string
  /** 文件的访问 URL */
  fileUrl: string
  /** 签名 URL 过期时间（秒） */
  expiration: number
}

/**
 * 批量获取签名 URL 的文件信息
 */
interface BatchUploadFileInfo {
  /** 文件名 */
  filename: string
  /** 文件 MIME 类型 */
  contentType: string
}

/**
 * 批量签名 URL 响应
 */
interface BatchUploadUrlsResponse {
  /** 签名 URL 列表 */
  urls: UploadUrlResponse[]
}

/**
 * 单文件上传进度回调
 */
export type SingleFileProgressCallback = (percent: number) => void

// ==================== 常量配置 ====================

/**
 * 上传配置常量
 */
export const UPLOAD_CONFIG = {
  /** 最大文件数量（支持 1 万张） */
  MAX_FILES: 10000,
  /** 最大文件大小（8MB） */
  MAX_FILE_SIZE: 8 * 1024 * 1024,
  /** 允许的文件类型 */
  ACCEPT_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as readonly string[],
  /** 并发上传数（优化：从 5 提升到 15） */
  CONCURRENCY: 15,
  /** 重试次数 */
  RETRY_ATTEMPTS: 3,
  /** 批量获取签名 URL 的最大数量 */
  BATCH_URL_LIMIT: 100,
} as const

// ==================== 工具函数 ====================

/**
 * 清理文件名，保留空格，仅移除控制字符
 * @param filename 原始文件名
 * @returns 清理后的文件名
 */
export function sanitizeUploadFilename(filename: string): string {
  return Array.from(filename)
    .filter((character) => {
      const code = character.charCodeAt(0)
      return code >= 32 && code !== 127
    })
    .join('')
}

export function createUploadSessionId(): string {
  if (
    typeof globalThis.crypto !== 'undefined' &&
    typeof globalThis.crypto.randomUUID === 'function'
  ) {
    return `upload-${globalThis.crypto.randomUUID().replace(/-/g, '')}`
  }

  return `upload-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// ==================== API 调用 ====================

/**
 * 获取上传签名 URL
 * @param filename 文件名
 * @param contentType 文件 MIME 类型
 * @returns 签名 URL 响应
 */
export async function getUploadUrl(
  filename: string,
  contentType: string,
  uploadSessionId?: string
): Promise<UploadUrlResponse> {
  // 清理文件名，保留空格
  const cleanFilename = sanitizeUploadFilename(filename)
  console.log('[UploadService] 请求签名 URL:', {
    filename: cleanFilename,
    contentType,
    uploadSessionId,
  })
  try {
    const response = await apiService.post<TaskApiResponse<UploadUrlResponse>>(
      '/api/oss/upload-url',
      {
        filename: cleanFilename,
        contentType,
        uploadSessionId,
      }
    )
    console.log('[UploadService] 获取签名 URL 成功:', response.data)
    return response.data
  } catch (error) {
    console.error('[UploadService] 获取签名 URL 失败:', error)
    throw error
  }
}

/**
 * 批量获取上传签名 URL
 * @param files 文件信息列表（每批最多 100 个）
 * @returns 签名 URL 响应列表
 */
export async function getBatchUploadUrls(
  files: BatchUploadFileInfo[],
  uploadSessionId?: string
): Promise<UploadUrlResponse[]> {
  if (files.length === 0) {
    return []
  }

  if (files.length > UPLOAD_CONFIG.BATCH_URL_LIMIT) {
    throw new Error(
      `单次批量获取签名 URL 最多支持 ${String(UPLOAD_CONFIG.BATCH_URL_LIMIT)} 个文件`
    )
  }

  console.log('[UploadService] 批量请求签名 URL:', {
    count: files.length,
    uploadSessionId,
  })
  try {
    // 清理文件名，保留空格
    const cleanFiles = files.map((f) => ({
      ...f,
      filename: sanitizeUploadFilename(f.filename),
    }))
    const response = await apiService.post<TaskApiResponse<BatchUploadUrlsResponse>>(
      '/api/oss/upload-urls',
      {
        files: cleanFiles,
        uploadSessionId,
      }
    )
    console.log('[UploadService] 批量获取签名 URL 成功:', {
      count: response.data.urls.length,
    })
    return response.data.urls
  } catch (error) {
    console.error('[UploadService] 批量获取签名 URL 失败:', error)
    throw error
  }
}

// ==================== 上传类 ====================

/**
 * 上传服务类
 * 管理文件上传流程，支持批量并发上传
 * 使用签名 URL 方式进行文件上传
 */
class UploadService {
  private abortController: AbortController | null = null
  private isAborted = false
  /** 当前活跃的 XMLHttpRequest 请求集合 */
  private activeRequests = new Set<XMLHttpRequest>()

  private hasAbortRequested(): boolean {
    return this.isAborted || this.abortController?.signal.aborted === true
  }

  /**
   * 使用 XMLHttpRequest 上传文件到签名 URL
   * 支持进度回调
   * @param file 文件
   * @param uploadUrl 签名 URL
   * @param onProgress 进度回调
   * @returns Promise<void>
   */
  private uploadWithProgress(
    file: File,
    uploadUrl: string,
    onProgress?: SingleFileProgressCallback
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      this.activeRequests.add(xhr)

      xhr.upload.onprogress = (event: ProgressEvent): void => {
        if (event.lengthComputable && onProgress !== undefined) {
          const percent = Math.round((event.loaded / event.total) * 100)
          onProgress(percent)
        }
      }

      xhr.onload = (): void => {
        this.activeRequests.delete(xhr)
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
        } else {
          reject(new Error(`上传失败: HTTP ${String(xhr.status)}`))
        }
      }

      xhr.onerror = (): void => {
        this.activeRequests.delete(xhr)
        reject(new Error('网络错误'))
      }

      xhr.onabort = (): void => {
        this.activeRequests.delete(xhr)
        reject(new Error('上传已取消'))
      }

      xhr.open('PUT', uploadUrl, true)
      xhr.setRequestHeader('Content-Type', file.type)
      xhr.send(file)
    })
  }

  /**
   * 上传单个文件到 OSS
   * @param file 文件
   * @param onProgress 进度回调
   * @returns 文件访问 URL
   */
  private async uploadSingleFile(
    file: File,
    uploadSessionId?: string,
    onProgress?: SingleFileProgressCallback
  ): Promise<string> {
    // 检查是否已取消
    if (this.hasAbortRequested()) {
      throw new Error('上传已取消')
    }

    // 获取签名 URL
    const { uploadUrl, fileUrl } = await getUploadUrl(file.name, file.type, uploadSessionId)

    // 再次检查是否已取消
    if (this.hasAbortRequested()) {
      throw new Error('上传已取消')
    }

    // 上传文件
    await this.uploadWithProgress(file, uploadUrl, onProgress)

    return fileUrl
  }

  /**
   * 上传单个文件（带重试和进度）
   * @param file 文件
   * @param onProgress 可选的进度回调
   * @returns 文件 URL
   */
  async uploadFile(
    file: File,
    onProgress?: SingleFileProgressCallback,
    uploadSessionId?: string
  ): Promise<string> {
    // 验证文件
    if (file.size > UPLOAD_CONFIG.MAX_FILE_SIZE) {
      throw new Error(`文件过大: ${file.name}`)
    }
    if (!UPLOAD_CONFIG.ACCEPT_TYPES.includes(file.type)) {
      throw new Error(`不支持的文件类型: ${file.type}`)
    }

    // 带重试上传
    let lastError: Error | null = null
    for (let i = 0; i < UPLOAD_CONFIG.RETRY_ATTEMPTS; i++) {
      try {
        // 检查是否已取消
        if (this.hasAbortRequested()) {
          throw new Error('上传已取消')
        }
        return await this.uploadSingleFile(file, uploadSessionId, onProgress)
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        // 如果是取消操作，不重试
        if (this.hasAbortRequested() || lastError.message === '上传已取消') {
          throw lastError
        }
        if (i < UPLOAD_CONFIG.RETRY_ATTEMPTS - 1) {
          // 等待后重试（指数退避）
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)))
        }
      }
    }

    throw lastError ?? new Error('上传失败')
  }

  /**
   * 批量上传文件
   * 使用并发控制，返回进度更新
   * @param files 文件列表
   * @param onProgress 进度回调
   * @returns 上传结果列表
   */
  async uploadBatch(
    files: File[],
    onProgress?: (progress: UploadProgress) => void
  ): Promise<UploadFileItem[]> {
    if (files.length > UPLOAD_CONFIG.MAX_FILES) {
      throw new Error(`文件数量超过限制: 最多 ${String(UPLOAD_CONFIG.MAX_FILES)} 个`)
    }

    // 重置取消状态
    this.isAborted = false
    this.abortController = new AbortController()

    // 初始化结果列表
    const results: UploadFileItem[] = files.map((file, index) => ({
      id: `file-${String(index)}-${String(Date.now())}`,
      file,
      status: 'pending' as const,
      previewUrl: URL.createObjectURL(file),
    }))

    let uploaded = 0
    let failed = 0

    // 并发上传控制
    const uploadWithConcurrency = async (): Promise<void> => {
      const queue = [...results]
      const executing = new Set<Promise<void>>()

      while ((queue.length > 0 || executing.size > 0) && !this.hasAbortRequested()) {
        // 填充执行队列
        while (
          executing.size < UPLOAD_CONFIG.CONCURRENCY &&
          queue.length > 0 &&
          !this.hasAbortRequested()
        ) {
          const item = queue.shift()
          if (item === undefined) {
            break
          }

          const promise = (async (): Promise<void> => {
            item.status = 'uploading'
            onProgress?.({
              total: files.length,
              uploaded,
              failed,
              currentFile: item.file.name,
              percent: Math.round(((uploaded + failed) / files.length) * 100),
            })

            try {
              item.url = await this.uploadFile(item.file)
              item.status = 'success'
              uploaded++
            } catch (error) {
              item.status = 'error'
              item.error =
                error instanceof Error ? error.message : String(error)
              failed++
            }

            onProgress?.({
              total: files.length,
              uploaded,
              failed,
              currentFile: '',
              percent: Math.round(((uploaded + failed) / files.length) * 100),
            })
          })()

          // 任务完成后自动从 Set 中移除
          const wrappedPromise = promise.finally(() => {
            executing.delete(wrappedPromise)
          })
          executing.add(wrappedPromise)
        }

        // 等待任一任务完成
        if (executing.size > 0) {
          await Promise.race(executing)
        }
      }

      // 等待所有正在执行的任务完成
      if (executing.size > 0) {
        await Promise.allSettled(executing)
      }
    }

    await uploadWithConcurrency()

    return results
  }

  /**
   * 使用预获取的签名 URL 上传文件
   * @param file 文件
   * @param uploadUrl 预获取的签名 URL
   * @param onProgress 进度回调
   * @returns Promise<void>
   */
  async uploadWithSignedUrl(
    file: File,
    uploadUrl: string,
    onProgress?: SingleFileProgressCallback
  ): Promise<void> {
    // 检查是否已取消
    if (this.hasAbortRequested()) {
      throw new Error('上传已取消')
    }

    // 带重试上传
    let lastError: Error | null = null
    for (let i = 0; i < UPLOAD_CONFIG.RETRY_ATTEMPTS; i++) {
      try {
        if (this.hasAbortRequested()) {
          throw new Error('上传已取消')
        }
        await this.uploadWithProgress(file, uploadUrl, onProgress)
        return
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error))
        if (this.hasAbortRequested() || lastError.message === '上传已取消') {
          throw lastError
        }
        if (i < UPLOAD_CONFIG.RETRY_ATTEMPTS - 1) {
          await new Promise((resolve) => setTimeout(resolve, 1000 * (i + 1)))
        }
      }
    }
    throw lastError ?? new Error('上传失败')
  }

  /**
   * 取消上传
   */
  cancelUpload(): void {
    this.isAborted = true
    if (this.abortController !== null) {
      this.abortController.abort()
      this.abortController = null
    }
    // 取消所有活跃的 XMLHttpRequest 请求
    this.activeRequests.forEach((xhr) => {
      xhr.abort()
    })
    this.activeRequests.clear()
  }

  /**
   * 重置上传状态
   */
  reset(): void {
    this.isAborted = false
    this.abortController = null
    this.activeRequests.clear()
  }
}

// 导出单例
export const uploadService = new UploadService()

// 导出类型
export type { UploadService }
